import { ROUTES } from '../helpers/contants';

export const isOwnerDepartment = (department: string): boolean => {
  const normalized = department?.trim().toLowerCase() ?? '';
  return (
    normalized === 'owner' ||
    normalized.endsWith(' owners') ||
    normalized === 'studio owners' ||
    normalized === 'production owners'
  );
};

export const getManageEntityMemberPageTitle = (
  memberType: string,
  isStudio = false,
): string => {
  if (memberType === 'CONTACT') return 'Contact Member';
  return isStudio ? 'Studio Employee' : 'Crew Member';
};

/** Manage-entities access rows: only direct memberships are bulk-removable / transferable. */
export interface ManageEntityAccessMemberFlags {
  userId?: string;
  isDirect?: boolean;
  isInherited?: boolean;
  inheritedFrom?: string | null | Record<string, unknown>;
  hasPendingApproval?: boolean;
  permissionType?: string;
  accessLevel?: string;
  userType?: string;
  isOwnerFromDoc?: boolean;
  hasViewOnlyAccess?: boolean;
}

export const MANAGE_ENTITY_ORDER_DEPENDENCY_WARNING =
  'Unable to remove user. The collaborator has order dependencies on this entity.';

export const hasManageEntityPendingApproval = (
  member: ManageEntityAccessMemberFlags,
): boolean => member?.hasPendingApproval === true;

/** Entity owner member (Production Owners list), not dept-level OWNER permission. */
export const isManageEntityOwnerMember = (
  member: ManageEntityAccessMemberFlags,
): boolean => member?.userType?.trim().toLowerCase() === 'owner';

/** Owner row: owner department, owner userType, or API `isOwnerFromDoc` (flat name/access sort). */
export const isManageEntityOwnerRow = (
  department: string,
  member: ManageEntityAccessMemberFlags,
): boolean =>
  isOwnerDepartment(department) ||
  isManageEntityOwnerMember(member) ||
  member?.isOwnerFromDoc === true;

/** Order-dependency callout: not shown for owner rows. */
export const shouldShowManageEntityOrderDependencyWarning = (
  department: string,
  member: ManageEntityAccessMemberFlags,
): boolean =>
  hasManageEntityPendingApproval(member) &&
  !isManageEntityOwnerRow(department, member);

export const isManageEntityInheritedMembership = (
  member: ManageEntityAccessMemberFlags,
): boolean => {
  if (member?.isInherited === true) return true;
  if (member?.isDirect === false) return true;
  return false;
};

/** Resolve API `inheritedFrom` (string or nested object) for tooltips. */
export const formatManageEntityInheritedFromLabel = (
  inheritedFrom: ManageEntityAccessMemberFlags['inheritedFrom'],
): string => {
  if (inheritedFrom == null) return '';

  if (typeof inheritedFrom === 'string') {
    return inheritedFrom.trim();
  }

  if (typeof inheritedFrom === 'number' || typeof inheritedFrom === 'boolean') {
    return String(inheritedFrom);
  }

  if (typeof inheritedFrom === 'object') {
    const o = inheritedFrom as Record<string, unknown>;
    const candidates = [
      o.name,
      o.title,
      o.label,
      o.entityName,
      o.productionName,
      o.displayName,
    ];
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) return c.trim();
    }
  }

  return '';
};

/** Tooltip when inherited members are greyed out in remove / transfer flows. */
export const getManageEntityInheritedMembershipTooltip = (
  member: ManageEntityAccessMemberFlags,
): string => {
  const label = formatManageEntityInheritedFromLabel(member?.inheritedFrom);
  if (label) return `Inherited from ${label}`;
  return 'Inherited access cannot be removed or reassigned from this screen.';
};

export const canSelectMemberForRemoveAccess = (
  department: string,
  member: ManageEntityAccessMemberFlags,
  currentUserId?: string,
): boolean =>
  !isManageEntityOwnerRow(department, member) &&
  member?.userId !== currentUserId &&
  !isManageEntityInheritedMembership(member) &&
  !hasManageEntityPendingApproval(member);

/** Restricted-access members cannot be made owners. */
export const isManageEntityRestrictedMember = (
  member: ManageEntityAccessMemberFlags,
): boolean => member?.accessLevel?.trim().toUpperCase() === 'RESTRICTED';

/** Shown inline on restricted rows (and as their tooltip) during transfer-ownership. */
export const MANAGE_ENTITY_RESTRICTED_TRANSFER_MESSAGE =
  "Restricted access — can't be assigned ownership";

export const canSelectMemberForTransferOwnership = (
  department: string,
  member: ManageEntityAccessMemberFlags,
  currentUserId?: string,
): boolean =>
  !isManageEntityOwnerRow(department, member) &&
  member?.userId !== currentUserId &&
  !isManageEntityInheritedMembership(member) &&
  !isManageEntityRestrictedMember(member);

/** Tooltip for members greyed out in the transfer-ownership flow only. */
export const getManageEntityTransferIneligibleTooltip = (
  department: string,
  member: ManageEntityAccessMemberFlags,
  currentUserId?: string,
): string | undefined => {
  if (isManageEntityOwnerRow(department, member)) return undefined;
  if (member?.userId === currentUserId) return undefined;
  if (isManageEntityInheritedMembership(member)) {
    return getManageEntityInheritedMembershipTooltip(member);
  }
  if (isManageEntityRestrictedMember(member)) {
    return MANAGE_ENTITY_RESTRICTED_TRANSFER_MESSAGE;
  }
  return undefined;
};

export const MANAGE_ENTITY_FROM = 'manage-entity';

export const appendManageEntityFromQuery = (url: string): string => {
  if (url.includes(`from=${MANAGE_ENTITY_FROM}`)) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}from=${MANAGE_ENTITY_FROM}`;
};

export const isFromManageEntity = (
  searchParams: Pick<URLSearchParams, 'get'>,
): boolean => searchParams.get('from') === MANAGE_ENTITY_FROM;

export const getManageEntityLabel = (entityType?: string): string => {
  if (entityType === 'studios' || entityType === 'studio') return 'studio';
  if (entityType === 'episodes' || entityType === 'episode') return 'episode';
  if (entityType === 'sets' || entityType === 'set') return 'set';
  if (entityType === 'locations' || entityType === 'location') {
    return 'location';
  }
  return 'production';
};

export const getManageEntityDashboardPath = (
  entityType?: string,
  entityId?: string,
): string => {
  switch (entityType) {
    case 'studios':
    case 'studio':
      return ROUTES.STUDIO_DASHBOARD;
    case 'productions':
    case 'production':
      return `${ROUTES.PRODUCTION_DASHBOARD}?${entityId || ''}`;
    case 'episodes':
    case 'episode':
      return `${ROUTES.EPISODE_DASHBOARD}?${entityId || ''}`;
    case 'sets':
    case 'set':
      return `${ROUTES.SET_DASHBOARD}?${entityId || ''}`;
    case 'locations':
    case 'location':
      return `${ROUTES.LOCATION_DASHBOARD}?${entityId || ''}`;
    default:
      return ROUTES.STUDIO_DASHBOARD;
  }
};

export const flattenMembersFromPermissions = (
  userPermissions?: Record<string, unknown[]>,
): any[] => {
  if (!userPermissions) return [];
  return Object.values(userPermissions).flatMap(members =>
    Array.isArray(members) ? members : [],
  );
};

/** Flatten manage-entities access payload (`userPermissions` or `members`). */
export const flattenManageEntitiesAccessMembers = (
  data?: Record<string, unknown> | null,
): any[] => {
  if (!data) return [];
  if (Array.isArray(data.members)) return data.members;
  return flattenMembersFromPermissions(
    data.userPermissions as Record<string, unknown[]> | undefined,
  );
};

/**
 * Legacy membership row id (_id / memberId). Bulk-remove uses `userIds` from
 * the access list; keep for other member APIs if needed.
 */
export const getManageEntityMemberRecordId = (
  member: Record<string, unknown> | null | undefined,
): string | undefined => {
  if (!member || typeof member !== 'object') return undefined;

  const raw =
    member._id ??
    member.id ??
    member.memberId ??
    member.memberShipId ??
    member.membershipId ??
    member.crewMemberId ??
    member.userId;

  if (raw == null || raw === '') return undefined;
  return String(raw);
};
